import React, { useState } from "react";
import { Play, RefreshCw, Check, AlertTriangle } from "lucide-react";
import { useTheme } from "../../../hooks/useTheme.js";
import { TUTORIAL_VIDEO_URL } from "../../../constants/secondaryCamera";
import StepHeader from "./StepHeader";
import GatedTutorialVideo from "./GatedTutorialVideo";

// Two phases on one step:
//   intro — tutorial video, then "Connect Secondary Camera"
//   pair  — QR, live status, and the pairing check behind "View Instructions"
//
// Pairing is verified once, on the continue click — the hook does not poll, so
// nothing here claims to detect the phone on its own.
const SETUP_STEPS = [
  "Position the device to capture your workspace",
  "Keep the camera active during the interview",
];

const SecondaryCameraConnectStep = ({
  qrLoading,
  qrError,
  qrThrottled,
  qrCode,
  generateSecondaryQR,
  checkPairingStatus,
  onPaired,
  onBack,
  stepNumber,
  totalSteps,
  // Owned by InterviewInstructions: this step unmounts when the candidate
  // moves on, and the watch gate must not reset if they navigate back.
  tutorialWatched,
  onTutorialWatched,
  tutorialVideoUrl = TUTORIAL_VIDEO_URL,
  tutorialPosterUrl,
}) => {
  const { theme } = useTheme();
  const isLight = theme === "light";

  const [showPairing, setShowPairing] = useState(false);
  const [checking, setChecking] = useState(false);
  // null = not checked yet, false = checked and not paired, true = confirmed
  const [paired, setPaired] = useState(null);
  // Nothing to watch when no clip is configured — don't gate on it.
  const canLeaveIntro = !tutorialVideoUrl || tutorialWatched;

  const handleContinue = async () => {
    setChecking(true);
    const isPaired = await checkPairingStatus();
    setChecking(false);
    setPaired(isPaired);
    if (isPaired) {
      onPaired();
    }
  };

  // force=true so a fresh QR is issued even when a phone is already paired.
  const handleRegenerate = () => {
    setPaired(null);
    generateSecondaryQR(true);
  };

  const cardClass = isLight
    ? "bg-white border-[#ECEDF5] shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
    : "bg-gray-800/50 border-gray-700/50 backdrop-blur-sm";
  const hairline = isLight ? "border-[#F1F2F7]" : "border-gray-700/50";
  const mutedText = isLight ? "text-slate-500" : "text-gray-400";
  const headingText = isLight ? "text-slate-900" : "text-white";
  const skeletonFill = isLight ? "bg-[#F1F2F5]" : "bg-gray-700/60";

  // Continue is only meaningful once there is something to pair against
  const canContinue =
    Boolean(qrCode?.qr_code) || Boolean(qrCode?.alreadyPaired);

  const renderPairingBody = () => {
    if (qrLoading && !qrCode) {
      return (
        <div className="animate-pulse">
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Mirrors the loaded layout — tile plus the regenerate action
                beneath it — so nothing shifts when the QR arrives. */}
            <div className="w-[176px] shrink-0">
              <div className={`w-full h-[176px] rounded-xl ${skeletonFill}`} />
              <div className={`mt-3 h-5 w-32 mx-auto rounded ${skeletonFill}`} />
            </div>
            <div className="flex-1 min-w-0 space-y-3 pt-1">
              <div className={`h-4 w-48 rounded ${skeletonFill}`} />
              <div className={`h-3 w-64 rounded ${skeletonFill}`} />
              <div className={`h-3 w-40 rounded ${skeletonFill}`} />
            </div>
          </div>
        </div>
      );
    }

    if (qrError) {
      return (
        <div className="flex flex-col items-center text-center py-8">
          <h3 className={`text-[15px] font-semibold mb-2 ${headingText}`}>
            {qrThrottled ? "One moment" : "Setup failed"}
          </h3>
          <p className={`text-[13px] mb-5 ${isLight ? "text-slate-500" : "text-gray-300"}`}>
            {qrError}
          </p>
          <button
            onClick={() => generateSecondaryQR(true)}
            className="px-6 py-2 rounded-full text-[13px] font-medium text-white bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90 transition-opacity"
          >
            Try again
          </button>
        </div>
      );
    }

    // Revisited after the round already finished — monitoring is moot.
    if (qrCode?.roundCompleted) {
      return (
        <div className="flex flex-col items-center text-center py-8">
          <h3 className={`text-[15px] font-semibold mb-2 ${headingText}`}>
            This step is already complete
          </h3>
          <p className={`text-[13px] ${mutedText}`}>
            Secondary camera monitoring for this step is done.
          </p>
        </div>
      );
    }

    // A phone is already connected for this step — no QR needed.
    if (qrCode?.alreadyPaired) {
      return (
        <div className="flex flex-col items-center text-center py-8">
          <span className="w-14 h-14 rounded-full flex items-center justify-center mb-4 bg-green-500/15 text-green-500">
            <Check className="w-7 h-7" />
          </span>
          <h3 className={`text-[15px] font-semibold mb-2 ${headingText}`}>
            Phone camera connected
          </h3>
          <p className={`text-[13px] mb-4 ${mutedText}`}>
            A phone is already paired and monitoring for this step.
          </p>
          <button
            onClick={handleRegenerate}
            disabled={qrLoading}
            className="text-[13px] font-medium text-blue-500 hover:text-blue-400 disabled:opacity-50"
          >
            Connect a different phone
          </button>
          {paired === false && (
            <p className="mt-4 text-[12px] text-amber-500">
              Phone camera not detected — finish setup on your phone, then continue.
            </p>
          )}
        </div>
      );
    }

    if (!qrCode?.qr_code) return null;

    return (
      <>
        <div className="flex flex-col sm:flex-row gap-6">
          {/* Fixed to the QR's own width so the regenerate action sits under
              the code it acts on, at both breakpoints. */}
          <div className="w-[176px] shrink-0">
            <div
              className={`w-full h-[176px] rounded-xl p-3 flex items-center justify-center ${
                isLight ? "bg-[#F1F2F5]" : "bg-gray-700/40"
              }`}
            >
              <img
                src={qrCode.qr_code}
                alt="Secondary camera pairing QR code"
                className="w-full h-full rounded-lg bg-white p-2"
              />
            </div>

            <button
              onClick={handleRegenerate}
              disabled={qrLoading}
              className={`mt-3 w-full text-[12px] font-medium flex items-center justify-center gap-1.5 transition-colors ${
                qrLoading
                  ? "opacity-50 cursor-not-allowed"
                  : "text-blue-500 hover:text-blue-400"
              }`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${qrLoading ? "animate-spin" : ""}`} />
              {qrLoading ? "Regenerating…" : "Regenerate QR"}
            </button>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className={`text-[15px] mt-3 font-semibold leading-snug ${headingText}`}>
              Scan QR Code
            </h3>
            <p className={`text-[14px] leading-relaxed mt-1.5 ${mutedText}`}>
              Point your phone's camera at the code — it opens the workspace
              camera in your phone's browser. Allow camera access when prompted.
            </p>
          </div>
        </div>

        <hr className={`my-5 ${hairline}`} />

        <div>
          {/* <h4 className={`text-[14px] font-medium mb-2.5 ${isLight ? "text-slate-700" : "text-gray-200"}`}>
            How to set up your Secondary Camera?
          </h4>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {SETUP_STEPS.map((step, i) => (
              <li key={step} className={`text-[14px] leading-relaxed ${mutedText}`}>
                {i + 1}. {step}
              </li>
            ))}
          </ol> */}

          {/* Pairing state reads as the outcome of the steps above it, so it
              closes the card rather than sitting beside the QR. */}
          <div className="mt-4">
            {paired === true ? (
              <p className="flex items-center gap-1.5 text-[12px] font-medium text-[#22C55E]">
                <span className="w-4 h-4 rounded-full bg-[#22C55E] flex items-center justify-center shrink-0">
                  <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                </span>
                Phone camera connected.
              </p>
            ) : paired === false ? (
              <p className="flex items-start gap-1.5 text-[14px] text-amber-500">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                Phone camera not detected — finish setup on your phone, then
                continue.
              </p>
            ) : (
              <p className="text-[14px] italic text-amber-500">
                Waiting for your phone to scan…
              </p>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      <StepHeader
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title="Connect your Secondary Camera"
      />

      <div className={`mt-3 rounded-2xl border ${cardClass}`}>
        {!showPairing ? (
          <>
            <div className="px-6 pt-2 pb-4">
              {/* <h2
                className={`text-[13px] font-medium ${
                  isLight ? "text-slate-700" : "text-gray-200"
                }`}
              >
                Watch Tutorial
              </h2> */}
            </div>

            <div className="px-6 pb-6">
              {tutorialVideoUrl ? (
                <GatedTutorialVideo
                  src={tutorialVideoUrl}
                  poster={tutorialPosterUrl}
                  watched={tutorialWatched}
                  onWatched={onTutorialWatched}
                />
              ) : (
                <div
                  className={`w-full aspect-[16/9] rounded-lg flex items-center justify-center ${
                    isLight ? "bg-[#F1F2F5]" : "bg-gray-700/40"
                  }`}
                >
                  <Play
                    className={`w-8 h-8 ${isLight ? "text-[#DDDFE6]" : "text-gray-600"}`}
                    strokeWidth={1.5}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="px-6 py-6">{renderPairingBody()}</div>
        )}
      </div>

      <div className="flex items-center justify-between mt-6">
        <button
          onClick={() => (showPairing ? setShowPairing(false) : onBack())}
          className={`px-7 py-2.5 rounded-full text-[14px] font-medium border transition-colors ${
            isLight
              ? "bg-white border-[#DDD8F0] text-slate-600 hover:bg-[#F7F7FC]"
              : "bg-transparent border-gray-600 text-gray-200 hover:bg-gray-700/40"
          }`}
        >
          Back
        </button>

        {!showPairing ? (
          <button
            onClick={() => setShowPairing(true)}
            disabled={!canLeaveIntro}
            title={
              canLeaveIntro ? undefined : "Watch the full tutorial to continue"
            }
            className={`px-7 py-2.5 rounded-full text-[14px] font-medium transition-opacity ${
              canLeaveIntro
                ? "text-white bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90"
                : "bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400"
            }`}
          >
            Connect Secondary Camera
          </button>
        ) : qrCode?.roundCompleted ? (
          <button
            onClick={onPaired}
            className="px-7 py-2.5 rounded-full text-[14px] font-medium text-white bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90 transition-opacity"
          >
            Next
          </button>
        ) : (
          <button
            onClick={handleContinue}
            disabled={checking || !canContinue}
            className={`px-7 py-2.5 rounded-full text-[14px] font-medium transition-opacity ${
              checking || !canContinue
                ? "bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400"
                : "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] text-white hover:opacity-90"
            }`}
          >
            {checking ? "Checking…" : "Next"}
          </button>
        )}
      </div>
    </div>
  );
};

export default SecondaryCameraConnectStep;
